import { INPUT_MONO } from "@/components/tool-kit/styles";
import type { Matrix } from "@/lib/tool-kit/linear-algebra/matrix";
import { formatStyled, type NumberStyle } from "@/lib/tool-kit/math/rational";

/**
 * A matrix drawn the way a textbook does: entries in a grid between tall brackets.
 *
 * `pivot` shades the cell a row operation is built around, and `split` draws the
 * vertical bar of an augmented matrix [A | I] after that many columns.
 */
export function MatrixView({
  matrix,
  label,
  style,
  mark,
  pivot,
  split,
}: {
  matrix: Matrix;
  /** The matrix's accessible name. */
  label: string;
  style: NumberStyle;
  mark: string;
  pivot?: { row: number; col: number };
  split?: number;
}) {
  return (
    <div className="inline-block max-w-full overflow-x-auto align-middle">
      <table
        aria-label={label}
        className="border-collapse border-foreground/70 border-x-2 font-mono text-foreground text-sm"
      >
        <tbody>
          {matrix.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: matrix rows are positional
            <tr key={i}>
              {row.map((value, j) => {
                const isPivot = pivot?.row === i && pivot.col === j;
                const bar = split !== undefined && j === split;
                return (
                  <td
                    className={`px-2.5 py-0.5 text-right tabular-nums ${bar ? "border-foreground/40 border-l" : ""} ${isPivot ? "rounded bg-primary/15 font-semibold" : ""}`}
                    // biome-ignore lint/suspicious/noArrayIndexKey: matrix cells are positional
                    key={j}
                  >
                    {formatStyled(value, style, mark)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A grid of text inputs for one matrix; only the first rows × cols cells are shown. */
export function MatrixGrid({
  idPrefix,
  rows,
  cols,
  cells,
  onChange,
  cellLabel,
}: {
  idPrefix: string;
  rows: number;
  cols: number;
  cells: readonly (readonly string[])[];
  onChange: (row: number, col: number, value: string) => void;
  /** The accessible name of cell (row, col), both one-based. */
  cellLabel: (row: number, col: number) => string;
}) {
  return (
    <div className="overflow-x-auto pb-1">
      <div
        className="inline-grid gap-1.5 border-foreground/50 border-x-2 px-2 py-1"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(3.5rem, 4.5rem))`,
        }}
      >
        {Array.from({ length: rows * cols }, (_, index) => {
          const row = Math.floor(index / cols);
          const col = index % cols;
          const id = `${idPrefix}-${row}-${col}`;
          return (
            <div key={id}>
              <label className="sr-only" htmlFor={id}>
                {cellLabel(row + 1, col + 1)}
              </label>
              <input
                className={`${INPUT_MONO} px-2 py-1.5 text-center`}
                id={id}
                inputMode="decimal"
                onChange={(event) => onChange(row, col, event.target.value)}
                value={cells[row]?.[col] ?? ""}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
